import React from "react";
import {
  Badge,
  Box,
  Button,
  Collapse,
  Flex,
  Grid,
  HStack,
  Icon,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  SimpleGrid,
  Text,
  VStack,
  useToken,
} from "@chakra-ui/react";
import { ChevronDownIcon, CloseIcon } from "@chakra-ui/icons";
import { ColumnInfo, Header } from "build-ts/metadata";
import { ColumnType, dataTypeToJSON } from "build-ts/enums";
import { MdFilterList } from "react-icons/md";
import OntologyEntitySelector from "./OntologyEntitySelector";

export type MappedSortOption = "mapping" | "name-asc" | "name-desc" | "created" | "modified";

const MAPPED_SORT_LABEL: Record<MappedSortOption, string> = {
  mapping: "Sort",
  "name-asc": "A→Z",
  "name-desc": "Z→A",
  created: "Created",
  modified: "Modified",
};

/** Mapped panel row: status · source (name + type) · arrow · ontology selector */
const MAPPED_ROW_GRID = "32px minmax(130px,0.95fr) 22px minmax(200px,1.35fr)";

const MAPPED_PANEL_SEARCH_MAX_W = "260px";

const MAP_PANEL_SCROLL_SX = {
  scrollbarWidth: "thin",
  scrollbarColor: "#CBD5E0 transparent",
  "&::-webkit-scrollbar": { width: "6px" },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": { backgroundColor: "#CBD5E0", borderRadius: "6px" },
  "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#A0AEC0" },
};

function columnIndexById(columns: ColumnInfo[], id: string | undefined): number {
  if (!id) return -1;
  return columns.findIndex(c => c.header?.id === id);
}

interface OntologyMappingRightPanelProps {
  rightPanelRowCount: number;
  mappedVisibleIds: string[];
  mappedSearchQuery: string;
  setMappedSearchQuery: (q: string) => void;
  mappedSortOption: MappedSortOption;
  setMappedSortOption: (opt: MappedSortOption) => void;
  columns: ColumnInfo[];
  isLogicalMapped: (col: ColumnInfo) => boolean;
  pendingPromoteIds: Set<string>;
  addedColumnIds: Set<string>;
  updatedColumnIds: Set<string>;
  kgColumns: Header[];
  dependentKgColumns: Map<string, Header[]>;
  getCombinedMetrics: (columnId: string) => Header[];
  onKgSelectionChange: (index: number, h: Header | null) => void;
  handleOpenCreateKGColumnModal: (columnType: ColumnType, columnIndex: number, defaultName: string) => void;
  handleOpenCreateMetricModal: (columnIndex: number, logicalColumn: ColumnInfo) => void;
  autoOpenColumnId: string | null;
  handleRightSelectorMenuClose: (columnId: string, hasKgSelection: boolean) => void;
  visibleRemovedColumns: ColumnInfo[];
  removedSectionOpen: boolean;
  setRemovedSectionOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setDismissedRemovedIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  /** Column IDs whose KG mapping or metrics have been changed but not yet saved. */
  unsavedMappingIds?: Set<string>;
  onDeleteMetricRequest?: (metricId: string) => Promise<Header[] | null>;
}

const OntologyMappingRightPanel: React.FC<OntologyMappingRightPanelProps> = ({
  rightPanelRowCount,
  mappedVisibleIds,
  mappedSearchQuery,
  setMappedSearchQuery,
  mappedSortOption,
  setMappedSortOption,
  columns,
  isLogicalMapped,
  pendingPromoteIds,
  addedColumnIds,
  updatedColumnIds,
  kgColumns,
  dependentKgColumns,
  getCombinedMetrics,
  onKgSelectionChange,
  handleOpenCreateKGColumnModal,
  handleOpenCreateMetricModal,
  autoOpenColumnId,
  handleRightSelectorMenuClose,
  visibleRemovedColumns,
  removedSectionOpen,
  setRemovedSectionOpen,
  setDismissedRemovedIds,
  unsavedMappingIds,
  onDeleteMetricRequest,
}) => {
  const [dotGreen500, dotGreen200, dotOrange600, dotOrange300, dotGray400, dotGray200] = useToken("colors", [
    "green.500",
    "green.200",
    "orange.600",
    "orange.300",
    "gray.400",
    "gray.200",
  ]);

  const mappedRowStatusDot = (opts: { mapped: boolean; isNew: boolean; pending: boolean }) => {
    const variant: "blue" | "green" | "gray" =
      opts.pending || (opts.isNew && opts.mapped) ? "blue" : opts.mapped ? "green" : "gray";

    let fill: string;
    let boxShadow: string;
    if (variant === "blue") {
      fill = dotOrange600;
      boxShadow = `0 0 6px ${dotOrange300}, 0 0 12px ${dotOrange300}`;
    } else if (variant === "green") {
      fill = dotGreen500;
      boxShadow = `0 0 6px ${dotGreen200}, 0 0 12px ${dotGreen200}`;
    } else {
      fill = dotGray400;
      boxShadow = `0 0 5px ${dotGray200}, 0 0 10px ${dotGray200}`;
    }

    return (
      <Box
        as="span"
        display="inline-block"
        w="10px"
        h="10px"
        borderRadius="full"
        bg={fill}
        flexShrink={0}
        aria-hidden
        boxShadow={boxShadow}
      />
    );
  };

  return (
    <Flex flex="1" minW={0} direction="column" pl={{ md: 3 }} pt={{ base: 2, md: 0 }}>
      <Flex align="center" gap={2} mb={2} flexShrink={0} w="100%" minW={0}>
        <Text fontSize="sm" fontWeight="800" color="gray.700" mb={0} flexShrink={0}>
          Mapped ({rightPanelRowCount})
        </Text>
        <Flex flex={1} justify="center" minW={0} px={1}>
          <Input
            w="100%"
            maxW={MAPPED_PANEL_SEARCH_MAX_W}
            size="sm"
            placeholder="Search..."
            value={mappedSearchQuery}
            onChange={e => setMappedSearchQuery(e.target.value)}
            bg="gray.50"
            borderRadius="md"
          />
        </Flex>
        <Menu placement="bottom-end" closeOnSelect>
          <MenuButton
            as={Button}
            size="sm"
            variant={mappedSortOption === "mapping" ? "outline" : "solid"}
            colorScheme={mappedSortOption === "mapping" ? "gray" : "purple"}
            flexShrink={0}
            fontWeight="600"
            leftIcon={<Icon as={MdFilterList} boxSize={4} aria-hidden />}
            rightIcon={<ChevronDownIcon boxSize={3} />}
          >
            {MAPPED_SORT_LABEL[mappedSortOption]}
          </MenuButton>
          <MenuList fontSize="sm" minW="220px" zIndex={30}>
            <MenuItem
              fontWeight={mappedSortOption === "mapping" ? "700" : "400"}
              icon={<Icon as={MdFilterList} boxSize={4} color="gray.500" />}
              onClick={() => setMappedSortOption("mapping")}
            >
              Default order
            </MenuItem>
            <MenuItem
              fontWeight={mappedSortOption === "name-asc" ? "700" : "400"}
              icon={<Icon as={MdFilterList} boxSize={4} color="gray.500" />}
              onClick={() => setMappedSortOption("name-asc")}
            >
              Name A→Z
            </MenuItem>
            <MenuItem
              fontWeight={mappedSortOption === "name-desc" ? "700" : "400"}
              icon={<Icon as={MdFilterList} boxSize={4} color="gray.500" />}
              onClick={() => setMappedSortOption("name-desc")}
            >
              Name Z→A
            </MenuItem>
            <MenuItem
              fontWeight={mappedSortOption === "created" ? "700" : "400"}
              icon={<Icon as={MdFilterList} boxSize={4} color="gray.500" />}
              onClick={() => setMappedSortOption("created")}
            >
              Created (newest)
            </MenuItem>
            <MenuItem
              fontWeight={mappedSortOption === "modified" ? "700" : "400"}
              icon={<Icon as={MdFilterList} boxSize={4} color="gray.500" />}
              onClick={() => setMappedSortOption("modified")}
            >
              Modified (newest)
            </MenuItem>
          </MenuList>
        </Menu>
      </Flex>

      <Box
        flex={1}
        minH={0}
        overflowY="auto"
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="md"
        sx={MAP_PANEL_SCROLL_SX}
      >
        {rightPanelRowCount === 0 ? (
          <Box py={8} px={3} textAlign="center">
            <Text color="gray.500" fontSize="sm" mb={0}>
              No mapped columns yet. Click a row on the left to assign ontology.
            </Text>
          </Box>
        ) : mappedVisibleIds.length === 0 ? (
          <Box py={8} px={3} textAlign="center">
            <Text color="gray.500" fontSize="sm" mb={0}>
              No columns match your search.
            </Text>
          </Box>
        ) : (
          mappedVisibleIds.map(id => {
            const index = columnIndexById(columns, id);
            if (index < 0) return null;
            const column = columns[index];
            const mapped = isLogicalMapped(column);
            const pending = pendingPromoteIds.has(id);
            const isNew = Boolean(id && addedColumnIds.has(id));
            const isUpdated = Boolean(id && updatedColumnIds.has(id));
            const needsOntologyAssignment = pending && !mapped;
            const isUnsaved = Boolean(id && unsavedMappingIds?.has(id));

            return (
              <Box
                key={`right-${id}`}
                px={2}
                py={2}
                bg={isUnsaved ? "yellow.50" : "white"}
                borderRadius="md"
                mx={needsOntologyAssignment ? 0 : 1}
                my={needsOntologyAssignment ? 0 : 0.5}
                borderLeftWidth={isUnsaved ? "3px" : undefined}
                borderLeftColor={isUnsaved ? "yellow.400" : undefined}
                {...(needsOntologyAssignment
                  ? { borderWidth: "2px", borderColor: "orange.300" }
                  : { borderBottomWidth: "1px", borderBottomColor: "gray.100" })}
              >
                <Grid templateColumns={MAPPED_ROW_GRID} gap={2} alignItems="flex-start" w="100%">
                  <Box display="flex" justifyContent="center" alignItems="center" minH="28px" overflow="visible" pt={2}>
                    {mappedRowStatusDot({ mapped, isNew, pending: needsOntologyAssignment })}
                  </Box>
                  <HStack minW={0} spacing={2} align="center" flexWrap="wrap">
                    <Text fontWeight="700" color="gray.900" mb={0} fontSize="sm" noOfLines={2}>
                      {column.header?.name || ""}
                    </Text>
                    {isNew && mapped && (
                      <Badge colorScheme="blue" fontSize="9px" variant="subtle">
                        new
                      </Badge>
                    )}
                    {isUpdated && (
                      <Badge colorScheme="orange" fontSize="9px" variant="subtle">
                        updated
                      </Badge>
                    )}
                    {isUnsaved && (
                      <Badge colorScheme="yellow" fontSize="9px" variant="solid">
                        unsaved
                      </Badge>
                    )}
                    <Badge fontSize="10px" colorScheme="gray" variant="subtle" flexShrink={0}>
                      {column.data_type !== null ? dataTypeToJSON(column?.data_type!) : "—"}
                    </Badge>
                  </HStack>
                  <Text color="gray.400" fontWeight="700" mb={0} textAlign="center" fontSize="sm" aria-hidden pt={2}>
                    →
                  </Text>
                  <Box minW={0}>
                    {(() => {
                      const kgColumn = dependentKgColumns.get(column.header?.id || "")?.[0] ?? null;
                      const metrics = getCombinedMetrics(column.header?.id!);
                      const hasMetrics = metrics.length > 0;

                      // Shared props for every OntologyEntitySelector in this row
                      const sharedSelectorProps = {
                        logicalColumnList: columns,
                        kgColumnList: kgColumns,
                        metricList: metrics,
                        kgSelection: kgColumn,
                        columnIndex: index,
                        logicalColumn: column,
                        dependentKgColumns,
                        onKgSelectionChange: (h: Header | null) => onKgSelectionChange(index, h),
                        onOpenCreateKGColumnModal: handleOpenCreateKGColumnModal,
                        onOpenCreateMetricModal: handleOpenCreateMetricModal,
                        onAfterMenuClose: (hasSel: boolean) => handleRightSelectorMenuClose(id, hasSel),
                        onDeleteMetricRequest,
                      };

                      if (!hasMetrics) {
                        // No metrics: single KG selector card spanning full width
                        return (
                          <OntologyEntitySelector {...sharedSelectorProps} autoOpenMenu={autoOpenColumnId === id} />
                        );
                      }

                      // All cases with metrics use a 2-col grid.
                      // KG card comes first when mapped; metric cards follow.
                      return (
                        <SimpleGrid columns={metrics.length === 1 && !kgColumn ? 1 : 2} spacing={2} w="100%">
                          {kgColumn && (
                            <OntologyEntitySelector {...sharedSelectorProps} autoOpenMenu={autoOpenColumnId === id} />
                          )}
                          {metrics.map(m => (
                            <OntologyEntitySelector
                              key={m.id}
                              {...sharedSelectorProps}
                              autoOpenMenu={false}
                              metricSelection={m}
                            />
                          ))}
                        </SimpleGrid>
                      );
                    })()}
                  </Box>
                </Grid>
              </Box>
            );
          })
        )}

        {visibleRemovedColumns.length > 0 && (
          <Box borderTopWidth="2px" borderColor="gray.200" mt={2} pt={2}>
            <Button
              variant="ghost"
              size="sm"
              w="100%"
              justifyContent="flex-start"
              fontWeight="700"
              color="gray.700"
              onClick={() => setRemovedSectionOpen(o => !o)}
              rightIcon={
                <Text as="span" mb={0} fontSize="xs">
                  {removedSectionOpen ? "▾" : "▸"}
                </Text>
              }
            >
              Removed ({visibleRemovedColumns.length})
            </Button>
            <Collapse in={removedSectionOpen} animateOpacity>
              <VStack align="stretch" spacing={0} mt={1}>
                {visibleRemovedColumns.map((column, rIndex) => (
                  <HStack
                    key={`removed-${column.header?.id ?? rIndex}`}
                    px={2}
                    py={2}
                    bg="red.50"
                    borderBottomWidth="1px"
                    borderColor="gray.100"
                    justify="space-between"
                    align="center"
                  >
                    <HStack minW={0} spacing={2} flex={1}>
                      <Text
                        fontWeight="600"
                        color="red.700"
                        mb={0}
                        fontSize="sm"
                        textDecoration="line-through"
                        noOfLines={1}
                      >
                        {column.header?.name || ""}
                      </Text>
                      <Badge colorScheme="red" fontSize="9px" variant="subtle" flexShrink={0}>
                        removed
                      </Badge>
                    </HStack>
                    <IconButton
                      aria-label="Dismiss removed column"
                      icon={<CloseIcon />}
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => {
                        const rid = column.header?.id;
                        if (rid) setDismissedRemovedIds(s => new Set(s).add(rid));
                      }}
                    />
                  </HStack>
                ))}
              </VStack>
            </Collapse>
          </Box>
        )}
      </Box>
    </Flex>
  );
};

export default OntologyMappingRightPanel;
